import React, { useState } from "react";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import {
  updateNoteById,
  deleteNoteById,
  shareNote,
} from "../store/slices/notesSlice";
import { fetchPublicUserProfile } from "../store/slices/userSlice";
import { Note } from "../types";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import ConfirmationModal from "./ConfirmationModal";
import { useToastContext } from "./ToastContext";
import AppSidebar from "./AppSidebar";

interface NoteDisplayProps {
  note: Note | null;
  loading: boolean;
  error: string | null;
  identifier: string;
  isURLFriendly: boolean;
  onBack: () => void;
}

const NoteDisplay: React.FC<NoteDisplayProps> = ({
  note,
  loading,
  error,
  identifier,
  isURLFriendly,
  onBack,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const currentUser = useAppSelector((state) => state.user.profile);
  const publicProfiles = useAppSelector((state) => state.user.publicProfiles);

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [editedVisibility, setEditedVisibility] = useState<
    "private" | "public"
  >("private");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  React.useEffect(() => {
    if (note) {
      trackPageVisit("note_detail", {
        note_id: identifier,
        note_title: note.title,
        is_url_friendly: isURLFriendly,
      });
    }
  }, [identifier, note, isURLFriendly]);

  React.useEffect(() => {
    if (note) {
      setEditedTitle(note.title);
      setEditedContent(note.content);
      setEditedVisibility(note.visibility);
    }
  }, [note]);

  React.useEffect(() => {
    if (
      note &&
      note.visibility === "public" &&
      note.userId &&
      !publicProfiles[note.userId]
    ) {
      dispatch(fetchPublicUserProfile(note.userId));
    }
  }, [note, publicProfiles, dispatch]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (note?.id) {
      const result = await dispatch(
        updateNoteById({
          id: note.id,
          updates: {
            title: editedTitle,
            content: editedContent,
            visibility: editedVisibility,
          },
        }),
      ).unwrap();

      trackUserInteraction("edit_note", "note_management", note.id);

      setIsEditing(false);

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }
    }
  };

  const handleCancel = () => {
    if (note) {
      setEditedTitle(note.title);
      setEditedContent(note.content);
      setEditedVisibility(note.visibility);
    }
    setIsEditing(false);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (note?.id) {
      trackUserInteraction("delete_note", "note_management", note.id);

      await dispatch(deleteNoteById(note.id));
      onBack();
    }
  };

  const handleShare = async () => {
    const url = note?.URLfriendlyname
      ? `${window.location.origin}/notes/n/${note.URLfriendlyname}`
      : `${window.location.origin}/note/${note?.id}`;

    // If sharing other user's public note, call the share API
    if (currentUser && note && note.userId !== currentUser.id && note.id) {
      try {
        await dispatch(shareNote(note.id));
      } catch (error) {
        console.error("Failed to share note via API:", error);
        // Continue with URL copying even if API fails
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setShowShareModal(true);
      setTimeout(() => setShowShareModal(false), 2000);
    } catch (error) {
      console.error("Failed to copy URL:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setShowShareModal(true);
        setTimeout(() => setShowShareModal(false), 2000);
      } catch (fallbackError) {
        console.error("Fallback copy failed:", fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading && !note) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#22c67a]/20 border border-[#22c67a]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl animate-spin">?</span>
          </div>
          <h2 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-1">
            {t("noteDetail.loadingNoteDetails")}
          </h2>
        </div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#22c67a]/20 border border-[#22c67a]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">??</span>
          </div>
          <h2 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-2">
            {t("noteDetail.noteNotFound")}
          </h2>
          <p className="text-[17px] text-gray-500 dark:text-white/40 mb-6">
            {t("noteDetail.noteNotFoundMessage")}
          </p>
          <button
            onClick={onBack}
            className="px-5 py-2.5 bg-[#22c67a] text-white text-[18px] font-semibold rounded-xl hover:opacity-90 transition-all"
          >
            {t("noteDetail.backToApp")}
          </button>
        </div>
      </div>
    );
  }

  const isOwner = currentUser && note.userId === currentUser.id;
  const authorName = isOwner
    ? t("noteDetail.you")
    : publicProfiles[note.userId]?.username ||
      `User ${note.userId.slice(0, 8)}...`;

  return (
    <>
      <MetaHead
        title={`${note.title} - Streak Spark`}
        description={`View note: ${note.title} in Streak Spark.`}
        keywords="note, details, view, edit, delete"
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        <div className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* TOPBAR */}
          <header className="flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={onBack}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#22c67a]/20 border border-[#22c67a]/30 flex items-center justify-center transition-all hover:bg-[#22c67a]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#22c67a"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("noteDetail.pageTitle")}
            </p>
            <div className="flex gap-2">
              {note.visibility === "public" && (
                <button
                  onClick={handleShare}
                  aria-label="Share note"
                  className="tap-scale w-8 h-8 rounded-[9px] bg-[#22c67a]/20 border border-[#22c67a]/30 flex items-center justify-center text-[#22c67a] transition-all hover:bg-[#22c67a]/30 text-sm"
                >
                  Share
                </button>
              )}
              {isOwner && !isEditing && (
                <button
                  onClick={handleEdit}
                  aria-label="Edit note"
                  className="tap-scale rounded-[9px] bg-[#22c67a]/20 border border-[#22c67a]/30 flex items-center justify-center text-[#22c67a] transition-all hover:bg-[#22c67a]/30 text-sm p-2"
                >
                  Edit
                </button>
              )}
            </div>
          </header>

          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-3xl mx-auto pb-8 space-y-4">
              <div className="rounded-2xl border border-[#22c67a]/20 dark:border-[#22c67a]/30 bg-gradient-to-br from-[#22c67a]/[0.07] to-transparent dark:from-[#22c67a]/[0.09] p-4 lg:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#22c67a]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[24px] leading-none">📝</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                        {note.title}
                      </p>
                      <p className="text-[16px] text-gray-600 dark:text-white/45">
                        {t("noteDetail.created")}: {formatDate(note.createdAt)}
                      </p>
                    </div>
                  </div>
                  <span className="text-[15px] font-semibold px-2.5 py-1 rounded-full shrink-0 bg-[#22c67a]/20 border border-[#22c67a]/30 text-[#22c67a]">
                    {note.visibility === "public"
                      ? t("noteDetail.visibilityPublic")
                      : t("noteDetail.visibilityPrivate")}
                  </span>
                </div>
              </div>

              {/* Note Card */}
              <div className="space-y-4">
                {/* Title */}
                <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                  <p className="text-[13px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/30 mb-1">
                    {t("noteDetail.title")}
                  </p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-black/[0.1] dark:border-white/[0.1] bg-black/[0.04] dark:bg-white/[0.06] text-[18px] text-gray-900 dark:text-white focus:outline-none focus:border-[#22c67a] transition-colors"
                    />
                  ) : (
                    <h2 className="text-[24px] font-semibold tracking-tight text-gray-900 dark:text-white">
                      {note.title}
                    </h2>
                  )}
                </div>

                {/* Content */}
                <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                  <p className="text-[13px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/30 mb-1">
                    {t("noteDetail.content")}
                  </p>
                  {isEditing ? (
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      rows={10}
                      className="w-full px-4 py-3 rounded-xl border border-black/[0.1] dark:border-white/[0.1] bg-black/[0.04] dark:bg-white/[0.06] text-[18px] text-gray-900 dark:text-white focus:outline-none focus:border-[#22c67a] transition-colors resize-vertical"
                    />
                  ) : (
                    <p className="text-[18px] text-gray-900 dark:text-white/80 leading-relaxed whitespace-pre-wrap">
                      {note.content}
                    </p>
                  )}
                </div>

                {/* Visibility toggle in edit mode */}
                {isEditing && (
                  <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/30 mb-1">
                      {t("noteDetail.visibility")}
                    </p>
                    <select
                      value={editedVisibility}
                      onChange={(e) =>
                        setEditedVisibility(
                          e.target.value as "private" | "public",
                        )
                      }
                      className="w-full px-4 py-3 rounded-xl border border-black/[0.1] dark:border-white/[0.1] bg-black/[0.04] dark:bg-white/[0.06] text-[18px] text-gray-900 dark:text-white focus:outline-none focus:border-[#22c67a] transition-colors"
                    >
                      <option value="private">
                        {t("noteDetail.visibilityPrivate")}
                      </option>
                      <option value="public">
                        {t("noteDetail.visibilityPublic")}
                      </option>
                    </select>
                  </div>
                )}

                {/* Meta row */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-black/[0.06] dark:border-white/[0.07]">
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                      {t("noteDetail.created")}
                    </p>
                    <p className="text-[17px] font-medium text-gray-900 dark:text-white">
                      {formatDate(note.createdAt)}
                    </p>
                  </div>
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3">
                    <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                      {t("noteDetail.visibility")}
                    </p>
                    <p className="text-[17px] font-medium text-gray-900 dark:text-white">
                      {note.visibility === "public"
                        ? t("noteDetail.visibilityPublic")
                        : t("noteDetail.visibilityPrivate")}
                    </p>
                  </div>
                  {note.visibility === "public" && (
                    <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded-xl p-3 col-span-2">
                      <p className="text-[15px] text-gray-500 dark:text-white/30 mb-0.5">
                        {t("noteDetail.createdBy")}
                      </p>
                      <p className="text-[17px] font-medium text-gray-900 dark:text-white">
                        {authorName}
                      </p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div>
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/30 mb-2">
                      {t("noteDetail.tags")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {note.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 text-[15px] font-medium bg-[#22c67a]/15 border border-[#22c67a]/25 text-[#22c67a] rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {isEditing ? (
                  <div className="flex gap-3 pt-4 border-t border-black/[0.07] dark:border-white/[0.08]">
                    <button
                      onClick={handleCancel}
                      className="flex-1 py-3 border border-black/[0.1] dark:border-white/[0.1] text-gray-700 dark:text-white/50 text-[18px] font-medium rounded-xl hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-all"
                    >
                      {t("noteDetail.cancel")}
                    </button>
                    <button
                      onClick={handleSave}
                      className="flex-1 py-3 bg-[#22c67a] text-white text-[18px] font-semibold rounded-xl hover:opacity-90 transition-all"
                    >
                      {t("noteDetail.save")}
                    </button>
                  </div>
                ) : isOwner ? (
                  <div className="flex gap-3 pt-4 border-t border-black/[0.07] dark:border-white/[0.08]">
                    {note.visibility === "public" && (
                      <button
                        onClick={handleShare}
                        className="flex-1 py-3 bg-[#22c67a]/15 border border-[#22c67a]/25 text-[#22c67a] text-[18px] font-medium rounded-xl hover:bg-[#22c67a]/25 transition-all"
                      >
                        {t("noteDetail.shareOnWeb")}
                      </button>
                    )}
                    <button
                      onClick={handleDelete}
                      className="py-3 px-4 bg-red-500/10 border border-red-500/20 text-red-500 text-[18px] font-medium rounded-xl hover:bg-red-500/20 transition-all"
                    >
                      Delete
                    </button>
                  </div>
                ) : note.visibility === "public" ? (
                  <div className="pt-4 border-t border-black/[0.07] dark:border-white/[0.08]">
                    <button
                      onClick={handleShare}
                      className="w-full py-3 bg-[#22c67a]/15 border border-[#22c67a]/25 text-[#22c67a] text-[18px] font-medium rounded-xl hover:bg-[#22c67a]/25 transition-all"
                    >
                      {t("noteDetail.shareOnWeb")}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title={t("noteDetail.deleteNoteTitle") as string}
        message={t("noteDetail.deleteNoteMessage") as string}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
      {showShareModal && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 bg-[#1a1a1f] text-white text-[16px] font-medium rounded-full shadow-lg">
          ? {t("noteDetail.linkCopied")}
        </div>
      )}
    </>
  );
};

export default NoteDisplay;
